import type MarkdownIt from 'markdown-it';

import type { IDMap } from '../../../convert-prosemirror-to-markdown/pm-markdown/serializer';
import type { FeatureToggles } from '../../../feature-keys';

export default function (
	md: MarkdownIt,
	idMap: IDMap,
	featureToggles: FeatureToggles,
	htmlConversionCounts: {
		htmlConvertedToNodes: number;
		htmlConvertedToFallbackNodes: number;
	},
) {
	const rule = md.inline.ruler.before('text', 'custom', function (state, silent) {
		const start = state.pos;
		const origSrc = state.src;
		const match = state.src.slice(start).match(
			/**
			 * Matches the following:
			 * <custom data-type="smartlink" data-id="123">content</custom>
			 * <custom data-id="123">content</custom>
			 * <custom data-type="emoji">content</custom>
			 * <custom>content</custom>
			 */
			/<custom(\sdata-(type|id)="(?<dataType>.*?)")?(\sdata-(type|id)="(?<dataId>.*?)")?>(?<dataContent>.*?)<\/custom>/,
		);

		if (!match) {
			return false;
		}

		const tagLength = match[0].length;
		const beforeTag = state.src.slice(start, state.src.indexOf(match[0]));
		const dataType = match.groups?.dataType;
		const dataId = match.groups?.dataId;
		const dataContent = match.groups?.dataContent;

		const afterTag = state.src.slice(start + beforeTag.length + tagLength);

		function mapCustomDataTypeToNodeType(customDataType?: string) {
			switch (customDataType) {
				case 'smartlink':
					return 'inlineCard';
				case 'date':
				case 'emoji':
				case 'status':
				case 'mention':
				case 'placeholder':
					return customDataType;
				// Just move below case alongside mention above to remove markdownPlusExtensions FF.
				case 'inlineExtension':
					return featureToggles.markdownPlusExtensions ? 'inlineExtension' : false;
				default:
					// TODO track invalid cards
					return false;
			}
		}
		const customDataType = mapCustomDataTypeToNodeType(dataType);

		if (!silent) {
			// tokenize beforeTag
			if (beforeTag) {
				state.src = beforeTag;
				// @ts-ignore
				state.md.inline.tokenize(state, beforeTag);
			}
			if (dataId && idMap?.[dataId]?.attributes && customDataType) {
				htmlConversionCounts.htmlConvertedToNodes++;
				const customDataToken = state.push(customDataType, '', 0);
				// @ts-ignore
				customDataToken.pmAttrs = idMap[dataId].attributes;
			} else {
				htmlConversionCounts.htmlConvertedToFallbackNodes++;

				const textToken = state.push('text', '', 0);

				// If there is no data content we want to hide the custom tags as the AI has hallucinated something
				if (!dataContent) {
					textToken.content = '';
				} else {
					textToken.content = dataContent || match[0];
				}
			}

			if (afterTag) {
				state.src = afterTag;
				state.pos = 0;
				// @ts-ignore
				state.md.inline.tokenize(state, afterTag);
			}
		}
		state.src = origSrc;
		state.pos = start + state.src.length;
		return true;
	});
	return rule;
}
